// IDB Quota Manager — cap rígido + LRU eviction.
//
// PROBLEMA: cleanup atual em info stores (vodInfoStore, seriesInfoStore)
// é SÓ por TTL (7 dias / 3 dias). Em sessão longa em parceiro com
// catálogo gigante, IDB pode encher antes do TTL — vlow Tizen 2017
// com quota ~50MB sofre primeiro. SO da TV mata o app quando IDB
// estoura quota → user perde tudo (favoritos, watch positions).
//
// SOLUÇÃO: este módulo é orquestrador. Roda dentro do health-watcher
// (1×/h pesado), lê navigator.storage.estimate(), decide política:
//   - <80% quota: sem ação (TTL pruning normal já cobre).
//   - 80-90% quota: cleanup proativo silencioso (pruneExpired + delete
//     snapshot stale).
//   - >90% quota: eviction agressiva LRU em info stores até cair pra 70%.
//
// Política de stores em ordem do MAIS sacrificável:
//   1. seriesInfoStore (TTL 3 dias, re-fetch ~500ms cold)
//   2. vodInfoStore (TTL 7 dias, idem)
//   3. catalogStore snapshot (re-download 5-15MB, cold ~5-15s)
//   4. local-db (NUNCA evict — favoritos/positions, perda irreversível)
//
// THRESHOLDS DINÂMICOS: % da quota relatada com floor 50MB / ceiling
// 500MB. Em vlow Tizen 2017 (~50MB quota): mantém threshold em
// proporção (40MB warn, 45MB critical). Em Chrome desktop (~1GB+):
// cap em 500MB (não desperdiça).
//
// FALLBACK Tizen 2017 (B1 do code review): Chromium 47 NÃO suporta
// navigator.storage.estimate (61+). Sem ele, computa pressão a partir
// de COUNT dos stores × heurística de bytes/record. Cenário-alvo da
// feature (vlow estoura quota); sem fallback, feature seria inútil
// justamente onde mais importa.

import { getCapability } from '@app-iptv/device'

import { catalogStore } from './catalog-store'
import { track } from './qoe'
import { providerPart } from './scope'
import { seriesInfoStore } from './series-info-store'
import { vodInfoStore } from './vod-info-store'

const FLOOR_MB = 50
const CEILING_MB = 500
const WARN_THRESHOLD_PCT = 80 // <80% sem ação
const CRITICAL_THRESHOLD_PCT = 90 // >90% eviction agressiva
const TARGET_AFTER_EVICTION_PCT = 70 // pós-eviction crítico

// Heurística: tamanho médio estimado por record (em KB).
// Calibração worst-case (M1 do code review):
// - vod-info: realista 5-25KB; usamos 12 pra evitar subestimar.
// - series-info: realista 30-200KB (varia muito por temporadas);
//   usamos 80 conservador-pra-cima. Eviction overshoots em count
//   é inofensivo (TTL recarrega no próximo open).
const VOD_AVG_KB = 12
const SERIES_AVG_KB = 80

// Rate-limit anti-loop (B2/M4 do code review): após action 'aggressive',
// próximas chamadas dentro deste intervalo rebaixam pra 'proactive' ou
// no-op. Evita 8 ciclos consecutivos de aggressive em sessão 8h+
// quando heurística subestima e pressão fica permanente.
const MIN_AGGRESSIVE_INTERVAL_MS = 3 * 60 * 60 * 1000 // 3h

let lastAggressiveAt = 0

interface QuotaStatus {
  usageMb: number
  quotaMb: number
  effectiveQuotaMb: number // quotaMb capado por FLOOR/CEILING
  pressurePct: number // usage / effectiveQuota
  source: 'navigator-storage' | 'count-fallback' // pra telemetria
}

/** Lê quota via navigator.storage.estimate (Chromium 61+). */
async function readQuotaFromStorageApi(): Promise<QuotaStatus | null> {
  if (!navigator.storage?.estimate) return null
  try {
    const est = await navigator.storage.estimate()
    if (est.usage == null || est.quota == null || est.quota <= 0) return null
    const usageMb = est.usage / 1024 / 1024
    const quotaMb = est.quota / 1024 / 1024
    const effectiveQuotaMb = Math.max(FLOOR_MB, Math.min(CEILING_MB, quotaMb))
    return {
      usageMb,
      quotaMb,
      effectiveQuotaMb,
      pressurePct: (usageMb / effectiveQuotaMb) * 100,
      source: 'navigator-storage',
    }
  } catch {
    return null
  }
}

/** Fallback: estima usage via count dos stores × bytes/record.
 *  Usado em Tizen 2017 / webOS antigos sem navigator.storage.estimate.
 *  Cenário-alvo da feature — sem este fallback ela não age onde
 *  mais importa.
 *
 *  effectiveQuotaMb = FLOOR_MB (50MB) — tier vlow tipicamente tem
 *  isso. Pessimista de propósito: se quota real é maior, eviction
 *  só dispara quando cache cresceu muito (defensável). Se quota
 *  real é menor, dispara mais cedo (correto pro objetivo). */
async function readQuotaFromCounts(): Promise<QuotaStatus | null> {
  try {
    const [vodCount, seriesCount] = await Promise.all([
      vodInfoStore.count(),
      seriesInfoStore.count(),
    ])
    const usageKb = vodCount * VOD_AVG_KB + seriesCount * SERIES_AVG_KB
    const usageMb = usageKb / 1024
    const effectiveQuotaMb = FLOOR_MB
    return {
      usageMb,
      quotaMb: effectiveQuotaMb,
      effectiveQuotaMb,
      pressurePct: (usageMb / effectiveQuotaMb) * 100,
      source: 'count-fallback',
    }
  } catch {
    return null
  }
}

async function readQuota(): Promise<QuotaStatus | null> {
  const fromApi = await readQuotaFromStorageApi()
  if (fromApi) return fromApi
  return readQuotaFromCounts()
}

interface QuotaActionResult {
  pressurePctBefore: number
  pressurePctAfter: number | null
  action: 'none' | 'proactive' | 'aggressive' | 'rate-limited'
  vodEvicted: number
  seriesEvicted: number
  snapshotsCleared: number
  durationMs: number
  source: 'navigator-storage' | 'count-fallback'
}

/** Política dinâmica:
 *  - <80%: 'none' (sem ação)
 *  - 80-90%: 'proactive' (pruneExpired)
 *  - >90%: 'aggressive' (eviction LRU até cair pra 70%) — rate-limited
 *    em MIN_AGGRESSIVE_INTERVAL_MS (3h) pra evitar ciclos repetidos.
 *
 *  Retorna o que foi feito + nova pressão pós-ação. */
export async function checkQuotaAndAct(): Promise<QuotaActionResult | null> {
  const startedAt = Date.now()
  const before = await readQuota()
  if (!before) return null

  const result: QuotaActionResult = {
    pressurePctBefore: Math.round(before.pressurePct),
    pressurePctAfter: null,
    action: 'none',
    vodEvicted: 0,
    seriesEvicted: 0,
    snapshotsCleared: 0,
    durationMs: 0,
    source: before.source,
  }

  if (before.pressurePct < WARN_THRESHOLD_PCT) {
    result.durationMs = Date.now() - startedAt
    return result
  }

  if (before.pressurePct < CRITICAL_THRESHOLD_PCT) {
    // 80-90%: proactive cleanup. pruneExpired (TTL/schema).
    result.action = 'proactive'
    const [vodPruned, seriesPruned] = await Promise.all([
      vodInfoStore.pruneExpired(),
      seriesInfoStore.pruneExpired(),
    ])
    result.vodEvicted = vodPruned
    result.seriesEvicted = seriesPruned
  } else {
    // >90%: aggressive eviction LRU. Rate-limit anti-loop:
    // se já rodou aggressive nas últimas 3h, rebaixa pra proactive.
    const timeSinceLastAggressive = Date.now() - lastAggressiveAt
    if (timeSinceLastAggressive < MIN_AGGRESSIVE_INTERVAL_MS) {
      result.action = 'rate-limited'
      // Roda proactive em vez (pelo menos pruneExpired).
      const [vodPruned, seriesPruned] = await Promise.all([
        vodInfoStore.pruneExpired(),
        seriesInfoStore.pruneExpired(),
      ])
      result.vodEvicted = vodPruned
      result.seriesEvicted = seriesPruned
    } else {
      result.action = 'aggressive'
      lastAggressiveAt = Date.now()

      // Calcular quanto liberar pra cair em TARGET_AFTER_EVICTION_PCT.
      const targetUsageMb = (TARGET_AFTER_EVICTION_PCT / 100) * before.effectiveQuotaMb
      const mbToFree = before.usageMb - targetUsageMb

      // mbToFree>0 garantido aqui (pressure>90% → usage>0.9*eff > 0.7*eff).
      // Estratégia: evict series primeiro (record maior, libera mais bytes
      // por evict). Se não bastar, vod.
      const seriesCount = await seriesInfoStore.count()
      const seriesEvictTarget = Math.min(
        seriesCount,
        Math.ceil((mbToFree * 1024) / SERIES_AVG_KB),
      )
      const seriesActuallyEvicted = await seriesInfoStore.evictLru(seriesEvictTarget)
      result.seriesEvicted = seriesActuallyEvicted
      const seriesMbFreed = (seriesActuallyEvicted * SERIES_AVG_KB) / 1024
      const remainingMbToFree = mbToFree - seriesMbFreed
      if (remainingMbToFree > 0) {
        const vodCount = await vodInfoStore.count()
        const vodEvictTarget = Math.min(
          vodCount,
          Math.ceil((remainingMbToFree * 1024) / VOD_AVG_KB),
        )
        const vodActuallyEvicted = await vodInfoStore.evictLru(vodEvictTarget)
        result.vodEvicted = vodActuallyEvicted
      }
      // catalogStore: eviction SCOPED ao provider atual (B3 do code review).
      // clear() sem args apagaria snapshots de TODOS providers — em
      // multi-provider isolation (commit 708f9a3), bug.
      //
      // Evicta só as surfaces sacrificáveis (vodItems/series) e PRESERVA
      // liveChannels: era `clear(scope)`, que derrubava o snapshot inteiro,
      // inclusive os ~391KB de liveChannels de onde o LiveHero tira o 1º frame.
      // Liberava espaço matando o caminho crítico do vídeo pra devolver a cota
      // que o VOD ocupava. VOD e séries degradam pra rede sem quebrar nada.
      const scope = providerPart()
      if (scope) {
        try {
          const evicted = await catalogStore.evictSacrificial(scope)
          result.snapshotsCleared = evicted.length > 0 ? 1 : 0
        } catch {
          // ignora
        }
      }
    }
  }

  // Re-medir pós-ação.
  const after = await readQuota()
  if (after) {
    result.pressurePctAfter = Math.round(after.pressurePct)
  }
  result.durationMs = Date.now() - startedAt

  if (import.meta.env.DEV) {
    console.warn('[idb-quota-manager]', result)
  }
  // Telemetria com discriminator `source: 'quota-manager'` (M5 do
  // code review) pra distinguir do cleanup_sample emitido pelo
  // runCleanup geral. Grafana agrega por source.
  track({
    type: 'cleanup_sample',
    ts: Math.floor(Date.now() / 1000),
    tier: getCapability('cpuTier'),
    cleanup_vod_pruned: result.vodEvicted,
    cleanup_series_pruned: result.seriesEvicted,
    cleanup_rq_removed: 0,
    cleanup_elapsed_ms: result.durationMs,
    cleanup_source: 'quota-manager',
    quota_action: result.action,
    quota_source: result.source,
    quota_pressure_before: result.pressurePctBefore,
    ...(result.pressurePctAfter !== null
      ? { quota_pressure_after: result.pressurePctAfter }
      : {}),
    ...(result.snapshotsCleared > 0 ? { snapshots_cleared: result.snapshotsCleared } : {}),
  })

  return result
}

/** Test helper — reseta rate-limit. NÃO usar em runtime. */
export function __resetQuotaManagerForTests(): void {
  lastAggressiveAt = 0
}
